const API_URL = "/api";

const MOVEMENT_LABELS = {
  opening: "Apertura",
  income: "Ingreso manual",
  sale_cash: "Venta en efectivo",
  expense: "Egreso manual",
  refund_cash: "Reintegro en efectivo",
  adjustment: "Ajuste",
};

const INCOME_TYPES = [
  "opening",
  "income",
  "sale_cash",
];

function getLogoUrl(logo) {
  if (!logo) {
    return "";
  }

  if (
    logo.startsWith("http://") ||
    logo.startsWith("https://") ||
    logo.startsWith("data:")
  ) {
    return logo;
  }

  return `${API_URL}${logo.startsWith("/") ? logo : `/${logo}`}`;
}

function money(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function dateTime(value) {
  if (!value) {
    return "No disponible";
  }

  return new Date(value).toLocaleString("es-CO");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function openCashClosingPrintWindow(report) {
  if (!report) {
    return false;
  }

  const printWindow = window.open(
    "",
    "_blank",
    "width=900,height=900"
  );

  if (!printWindow) {
    return false;
  }

  const difference = Number(report.difference_amount || 0);
  const isBalanced = difference === 0;

  const differenceLabel = isBalanced
    ? "Caja cuadrada"
    : difference > 0
      ? "Sobrante"
      : "Faltante";

  const differenceClass = isBalanced
    ? "balanced"
    : "unbalanced";

  const logoUrl = getLogoUrl(report.company_logo);

  const denominationRows = (report.denominations || [])
    .map((item) => {
      const denomination = Number(item.denomination || 0);
      const quantity = Number(item.quantity || 0);
      const subtotal = denomination * quantity;

      return `
        <tr>
          <td>${money(denomination)}</td>
          <td class="align-right">${quantity}</td>
          <td class="align-right">${money(subtotal)}</td>
        </tr>
      `;
    })
    .join("");

  const movementRows = (report.movements || [])
    .map((movement) => {
      const movementType =
        movement.movement_type || movement.type || "";

      const movementName =
        movement.type ||
        MOVEMENT_LABELS[movementType] ||
        movementType ||
        "Movimiento";

      const isIncome = INCOME_TYPES.includes(movementType);

      return `
        <tr>
          <td>
            <strong>${escapeHtml(movementName)}</strong>
            <small>${escapeHtml(
              movement.concept || "Sin concepto"
            )}</small>
          </td>
          <td>${escapeHtml(
            dateTime(movement.date || movement.created_at)
          )}</td>
          <td class="align-right ${
            isIncome ? "income" : "expense"
          }">
            ${isIncome ? "+" : "-"}${money(movement.amount)}
          </td>
        </tr>
      `;
    })
    .join("");

  const denominationSection = denominationRows
    ? `
      <section class="section">
        <h3>Conteo por denominación</h3>

        <table>
          <thead>
            <tr>
              <th>Denominación</th>
              <th class="align-right">Cantidad</th>
              <th class="align-right">Subtotal</th>
            </tr>
          </thead>

          <tbody>
            ${denominationRows}

            <tr class="total-row">
              <td colspan="2">Total contado</td>
              <td class="align-right">
                ${money(report.counted_amount)}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    `
    : "";

  const movementsSection = movementRows
    ? `
      <section class="section">
        <h3>Movimientos realizados</h3>

        <table>
          <thead>
            <tr>
              <th>Movimiento</th>
              <th>Fecha</th>
              <th class="align-right">Valor</th>
            </tr>
          </thead>

          <tbody>
            ${movementRows}
          </tbody>
        </table>
      </section>
    `
    : "";

  const noteSection = report.closing_note
    ? `
      <section class="note">
        <strong>Observación de cierre</strong>
        <p>${escapeHtml(report.closing_note)}</p>
      </section>
    `
    : "";

  printWindow.onload = () => {
    const logo = printWindow.document.querySelector(".company-logo");

    const printDocument = () => {
      printWindow.focus();
      window.setTimeout(() => printWindow.print(), 300);
    };

    if (logo && !logo.complete) {
      logo.onload = printDocument;
      logo.onerror = printDocument;
      return;
    }

    printDocument();
  };

  printWindow.document.open();

  printWindow.document.write(`
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>
          Cierre y arqueo de caja #${escapeHtml(report.session_id)}
        </title>

        <style>
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 30px;
            color: #18324d;
            background: #ffffff;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
          }

          .receipt {
            max-width: 800px;
            margin: 0 auto;
          }

          .brand {
            display: flex;
            align-items: center;
            gap: 18px;
            padding-bottom: 18px;
            border-bottom: 2px solid #e6008d;
          }

          .company-logo {
            width: 72px;
            height: 72px;
            object-fit: contain;
            border-radius: 10px;
          }

          .eyebrow {
            margin: 0 0 5px;
            color: #e6008d;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.08em;
          }

          .brand h1 {
            margin: 0 0 6px;
            color: #18324d;
            font-size: 21px;
          }

          .brand p {
            margin: 3px 0;
            color: #64748b;
            font-size: 11px;
          }

          .title {
            margin: 24px 0 18px;
            padding: 17px 20px;
            border-radius: 12px;
            color: #ffffff;
            background: #e6008d;
          }

          .title small {
            display: block;
            opacity: 0.86;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.05em;
          }

          .title h2 {
            margin: 5px 0 0;
            font-size: 21px;
          }

          .people,
          .summary {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 14px;
          }

          .summary {
            grid-template-columns: repeat(3, 1fr);
          }

          .card {
            padding: 13px;
            border: 1px solid #dbe5ec;
            border-radius: 10px;
            background: #f8fafc;
          }

          .card span,
          .card small {
            display: block;
            color: #64748b;
            font-size: 10px;
          }

          .card strong {
            display: block;
            margin: 5px 0;
            color: #18324d;
            font-size: 13px;
          }

          .card.balanced {
            border-color: #a7f3d0;
            background: #ecfdf5;
          }

          .card.balanced strong {
            color: #047857;
          }

          .card.unbalanced {
            border-color: #fecaca;
            background: #fff7f7;
          }

          .card.unbalanced strong {
            color: #dc2626;
          }

          .section {
            margin-top: 24px;
          }

          .section h3 {
            margin: 0 0 9px;
            color: #18324d;
            font-size: 14px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th,
          td {
            padding: 9px 8px;
            border-bottom: 1px solid #e5edf3;
            text-align: left;
          }

          th {
            color: #64748b;
            font-size: 9px;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }

          td strong,
          td small {
            display: block;
          }

          td small {
            margin-top: 3px;
            color: #64748b;
            font-size: 10px;
          }

          .align-right {
            text-align: right;
          }

          .total-row td {
            color: #18324d;
            background: #f1f7fb;
            font-size: 13px;
            font-weight: 800;
          }

          .income {
            color: #15803d;
            font-weight: 800;
          }

          .expense {
            color: #dc2626;
            font-weight: 800;
          }

          .note {
            margin-top: 24px;
            padding: 14px;
            border-left: 4px solid #e6008d;
            background: #f8fafc;
          }

          .note p {
            margin: 6px 0 0;
            color: #64748b;
            line-height: 1.45;
          }

          .signatures {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 60px;
            margin-top: 65px;
          }

          .signature {
            padding-top: 10px;
            border-top: 1px solid #334155;
            color: #334155;
            font-size: 11px;
            font-weight: 700;
            text-align: center;
          }

          footer {
            margin-top: 30px;
            padding-top: 14px;
            border-top: 1px solid #e5edf3;
            color: #94a3b8;
            font-size: 10px;
            text-align: center;
          }

          @media print {
            body {
              padding: 0;
            }

            .receipt {
              max-width: none;
            }
          }
        </style>
      </head>

      <body>
        <main class="receipt">
          <header class="brand">
            ${
              logoUrl
                ? `
                  <img
                    class="company-logo"
                    src="${escapeHtml(logoUrl)}"
                    alt="Logo de empresa"
                  />
                `
                : ""
            }

            <div>
              <p class="eyebrow">COMPROBANTE OPERATIVO</p>
              <h1>${escapeHtml(report.company_name || "Mi empresa")}</h1>

              ${
                report.company_nit
                  ? `<p>NIT: ${escapeHtml(report.company_nit)}</p>`
                  : ""
              }

              ${
                report.company_address
                  ? `<p>${escapeHtml(report.company_address)}</p>`
                  : ""
              }

              ${
                report.company_phone || report.company_email
                  ? `
                    <p>
                      ${escapeHtml(
                        [report.company_phone, report.company_email]
                          .filter(Boolean)
                          .join(" · ")
                      )}
                    </p>
                  `
                  : ""
              }
            </div>
          </header>

          <section class="title">
            <small>CIERRE Y ARQUEO DE CAJA</small>
            <h2>Comprobante #${escapeHtml(report.session_id)}</h2>
          </section>

          <section class="people">
            <div class="card">
              <span>Abierta por</span>
              <strong>${escapeHtml(report.opened_by || "Sistema")}</strong>
              <small>${escapeHtml(dateTime(report.opened_at))}</small>
            </div>

            <div class="card">
              <span>Cerrada por</span>
              <strong>${escapeHtml(report.closed_by || "Sistema")}</strong>
              <small>${escapeHtml(dateTime(report.closed_at))}</small>
            </div>
          </section>

          <section class="summary">
            <div class="card">
              <span>Saldo esperado</span>
              <strong>${money(report.expected_amount)}</strong>
            </div>

            <div class="card">
              <span>Efectivo contado</span>
              <strong>${money(report.counted_amount)}</strong>
            </div>

            <div class="card ${differenceClass}">
              <span>${differenceLabel}</span>
              <strong>${money(Math.abs(difference))}</strong>
            </div>
          </section>

          <section class="section">
            <h3>Resumen del arqueo</h3>

            <table>
              <tbody>
                <tr>
                  <td>Base de apertura</td>
                  <td class="align-right">${money(report.opening_amount)}</td>
                </tr>
                <tr>
                  <td>Ventas en efectivo</td>
                  <td class="align-right">${money(report.sales_income)}</td>
                </tr>
                <tr>
                  <td>Ingresos manuales</td>
                  <td class="align-right">${money(report.manual_income)}</td>
                </tr>
                <tr>
                  <td>Reintegros en efectivo</td>
                  <td class="align-right">-${money(report.refunds)}</td>
                </tr>
                <tr>
                  <td>Egresos manuales</td>
                  <td class="align-right">-${money(report.manual_expense)}</td>
                </tr>
                <tr class="total-row">
                  <td>Saldo esperado</td>
                  <td class="align-right">${money(report.expected_amount)}</td>
                </tr>
                <tr>
                  <td>Efectivo contado</td>
                  <td class="align-right">${money(report.counted_amount)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          ${denominationSection}
          ${movementsSection}
          ${noteSection}

          <section class="signatures">
            <div class="signature">
              Cajero: ${escapeHtml(report.closed_by || "")}
            </div>

            <div class="signature">
              Supervisor
            </div>
          </section>

          <footer>
            Generado por NetLedger · Documento de control interno
          </footer>
        </main>
      </body>
    </html>
  `);

  printWindow.document.close();

  return true;
}